import { ApiError, type LegacyApiError } from '@omnipresentgroup/api';
import { IS_E2E } from '@omnipresentgroup/constants';
import { notifyError } from '@omnipresentgroup/design-system';
import { NotifyErrorDescription } from '@omnipresentgroup/shared';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import { useIntercom } from 'react-use-intercom';

import { environment } from '../../../environment';
import {
  DEFAULT_ERROR_DESCRIPTION,
  DEFAULT_ERROR_TITLE,
} from '../../constants';

export const WrappedQueryClientProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { update } = useIntercom();

  const intercomEnabled = !IS_E2E && !!environment.intercomAppId;

  const queryClient = useMemo(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error) => {
            error instanceof ApiError &&
              intercomEnabled &&
              update({
                customAttributes: { error_trace_id: error.traceId },
              });
          },
        }),
        mutationCache: new MutationCache({
          onError: (error: unknown, _variables, _context, mutation) => {
            // if the userland as a handler use it
            if (mutation.options.onError) {
              return;
            }
            if (error instanceof ApiError) {
              notifyError({
                title: error.title,
                description: <NotifyErrorDescription error={error} />,
              });
              intercomEnabled &&
                error.traceId &&
                update({
                  customAttributes: { error_trace_id: error.traceId },
                });
            } else {
              notifyError({
                title: DEFAULT_ERROR_TITLE,
                description:
                  (error as Error).message || DEFAULT_ERROR_DESCRIPTION,
              });
            }
          },
        }),
        defaultOptions: {
          queries: {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            retry: false,
            networkMode: 'always',
            throwOnError: true,
            retryOnMount: false,
          },
          mutations: {
            throwOnError: (error) =>
              (error as ApiError | LegacyApiError).status >= 500,
          },
        },
      }),
    [intercomEnabled, update],
  );

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};
